import React from 'react';
import {Tooltip, OverlayTrigger} from 'react-bootstrap';

import Breadcrumbs from '../../components/Breadcrumbs';
import Button, {ButtonSizes, ButtonTypes} from '../../components/ButtonNew';
import YouTubeVideo from '../../components/YouTubeVideo';
import {Badge} from '../../components/Badge';
import SimpleRating from '../../components/SimpleRating';
import certificates from '../../constants/certificates';
import LinkedInCertURLGenerator from '../../util/LinkedInCertURLGenerator';

import {useAppContext, useAnalytics} from '../../hooks';

import {CourseIcon} from './CourseIcon';

// track slug : url to form e.g. 'data-engineering': 'https://airtable.com/appROifRg71Nsizjd/shrIfLdM81zpZsWoV',
const waitlistForms = {};

const CourseHero = ({
  course,
  nextLessonURL,
  upgradeURL,
  monthlyPrice,
  onClickContinue,
  onClickUpgrade,
  studentCount,
}) => {
  const {hasSubscription} = useAppContext();
  const {tracker} = useAnalytics();

  const {
    slug,
    title,
    shortTitle,
    description,
    featureList,
    trailer,
    thumbnail,
    earlyAccess,
    lessonsComplete,
    lessonsTotal,
    percentComplete,
  } = course;

  const certExists = slug in certificates;
  const waitlistUrl = waitlistForms?.[slug] ?? null;

  const currentDate = new Date();
  const certInfo = {
    certName: title,
    issueYear: currentDate.getFullYear(),
    issueMonth: currentDate.getMonth() + 1,
    certUrl: `https://www.tryexponent.com/certificates/${slug}`,
  };
  const addToLinkedInURL = LinkedInCertURLGenerator(certInfo);

  return (
    <div className="py-4 md:py-12 flex w-full items-center justify-center" style={{backgroundColor: '#F5F5FA'}}>
      <div className="px-4 max-w-6xl mx-auto items-center grid grid-cols-12">
        <div className="col-span-12 md:col-span-6">
          <Breadcrumbs
            items={[
              {name: 'Courses', url: '/courses'},
              {name: shortTitle, url: `/courses/${slug}`},
            ]}
          />
          {earlyAccess && (
            <Badge className="hidden md:inline-flex mb-2 self-start cursor-pointer">
              <OverlayTrigger
                placement="right"
                overlay={(
                  <Tooltip>
                    Get early access to this course with new lessons and videos coming soon.
                  </Tooltip>
                )}
              >
                <span>Early access</span>
              </OverlayTrigger>
            </Badge>
          )}
          {/* <h1 className="text-4xl md:text-5xl text-black font-extrabold mb-5 mt-2"> */}
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
            {shortTitle}
          </h1>

          <div className="mt-4 text-base leading-6 text-gray-600 mb-4">
            {description}
          </div>

          {/* Feature list */}
          <div className="my-4 space-y-1">
            {featureList?.map((feature) => (
              <div className="flex items-center space-x-1.5 text-indigo-600" key={feature.name}>
                <CourseIcon name={feature.icon} size="small" />
                <span className="text-gray-700 text-base font-regular text-ellipsis truncate">{feature.name}</span>
              </div>
            ))}
          </div>

          {/* Rating */}
          <div className="my-6">
            <SimpleRating studentCount={studentCount || 1000} />
          </div>

          {waitlistUrl ? (
            <Button
              buttonType={ButtonTypes.PRIMARY}
              size={ButtonSizes.LARGE}
              href={waitlistUrl}
              target="_blank"
              className="px-4 mr-2 mb-2 whitespace-nowrap"
            >
              Join our waitlist
            </Button>
          ) : (
            <div className="flex flex-col md:flex-row sm:items-center">
              {!hasSubscription && (
              <Button
                size={ButtonSizes.LARGE}
                href={upgradeURL}
                className="text-sm px-4 mr-2 mb-2 sm:max-w-xs flex-grow whitespace-nowrap"
                onClick={onClickUpgrade}
              >
                {`Get all access for ${monthlyPrice} / month`}
              </Button>
              )}
              {nextLessonURL && (
              <Button
                buttonType={hasSubscription ? ButtonTypes.PRIMARY : ButtonTypes.TERTIARY}
                href={nextLessonURL}
                className="px-4 mr-2 mb-2 whitespace-nowrap"
                onClick={onClickContinue}
              >
                {hasSubscription ? 'Continue learning' : 'Try free preview'}
              </Button>
              )}
              {/* Progress tracker */}
              {hasSubscription && lessonsComplete < lessonsTotal && (
              <div className="mb-2 md:ml-2 text-gray-500 text-sm font-medium">
                <CourseIcon name="cap" className="mr-1" />
                {`${lessonsComplete}/${lessonsTotal} lessons completed (${percentComplete}%)`}
              </div>
              )}
              {/* Certificate of completion */}
              {hasSubscription && lessonsComplete === lessonsTotal && certExists && (
              <Button
                href={addToLinkedInURL}
                className="px-4 mr-2 mb-2 whitespace-nowrap"
                buttonType={ButtonTypes.TERTIARY}
                target="_blank"
                onClick={() => tracker.courseClickAddToLinkedin({courseID: slug, type: 'course hero'})}
              >
                Claim certificate
              </Button>
              )}
            </div>
          )}
        </div>

        {/* Trailer */}
        <div className="hidden md:flex col-span-6">
          {trailer && (
            <div className="w-full rounded-xl overflow-hidden md:ml-12">
              <YouTubeVideo videoID={trailer} />
            </div>
          )}
          {!trailer && thumbnail && (
            <img
              src={`${thumbnail}?w=800&h=800`}
              alt=""
              className="w-full rounded-xl overflow-hidden md:ml-12"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseHero;
